import React, { memo, useMemo, useState } from "react";
import {
  Grid,
  Popover,
  makeStyles,
  OutlinedInput,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Typography,
  Box
} from "@material-ui/core";
import { connect } from "react-redux";
import { useSnackbar } from "notistack";

import { FormattedText } from "../common/FormattedText";
import API from "../../utils/axios";
import { client } from "../map/DriverMarkers";
import { setSolution } from "../../store/actions/solution";
import { setLoadingRoutesList } from "../../store/actions/loading";
import { openAddVehicleDialog } from "../../store/actions/common";
import { DirectionsCar } from "@material-ui/icons";
import { routeColors } from "../data/constants";
import OptimizedTooltip from "../common/OptimizedTooltip";
import useIsTruncated from "../optimized/useIsTruncated";
import { cleanSolutionWithNewRoutes } from "../../store/actions/custom";

const useStyles = makeStyles(theme => ({
  menuPaper: {
    width: 320,
    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.16)"
  },
  menuContentWrapper: {
    overflow: "hidden"
  },
  searchWrapper: {
    padding: theme.spacing(1.25, 1.1),
    borderBottom: "1px solid #EAEBF0"
  },
  searchInput: {
    padding: theme.spacing(1.5)
  },
  noVehicleNotice: {
    fontSize: theme.typography.pxToRem(14),
    color: "#202124",
    textAlign: "center",
    padding: theme.spacing(1.125, 0)
  },
  listWrapper: {
    padding: theme.spacing(1, 0),
    maxHeight: 216,
    overflowY: "auto"
  },
  list: {
    padding: 0
  },
  listItem: {
    padding: theme.spacing(0.93, 2.25),
    "& .more-info-icon": {
      opacity: 0,
      visibility: "hidden",
      color: "#9D9EA2"
    },
    "&:hover .more-info-icon": {
      opacity: 1,
      visibility: "visible"
    }
  },
  listItemAvatar: {
    minWidth: 42
  },
  vehicleAvatar: {
    width: 26,
    height: 26,
    backgroundColor: "#1329FE",
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium
  },
  noVehicleAvatarIcon: {
    fontSize: theme.typography.pxToRem(31),
    color: "#C2C3C7"
  },
  listItemText: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    maxWidth: 200,
    paddingRight: 10,
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "left",
    color: "#202124"
  },
  vehicleDetailsTitle: {
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: 500,
    fontSize: "0.825rem",
    lineHeight: "1rem",
    color: "#FFFFFF"
  },
  vehicleDetailsContent: {
    paddingTop: "0.25rem",
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "0.825rem",
    lineHeight: "1rem",
    color: "#FFFFFF"
  },
  vehicleDetailsTooltip: {
    width: 248,
    maxWidth: 248,
    padding: theme.spacing(1.25, 1.5)
  },
  newVehicleWrapper: {
    height: 56,
    maxHeight: 56,
    borderTop: "1px solid #EAEBF0",
    padding: theme.spacing(1, 0)
  },
  newVehicleButton: {
    width: "100%",
    color: theme.palette.primary.main,
    padding: theme.spacing(1.25, 1.85),
    textAlign: "left",
    fontSize: theme.typography.pxToRem(15),
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: "#F4F4F4"
    }
  },
  newVehicleIcon: {
    marginRight: theme.spacing(2)
  },
  truckIconLink: {
    boxSizing: "border-box",
    display: "block",
    width: 24,
    height: 24,
    borderRadius: 4,
    paddingTop: 3.5,
    paddingLeft: 5
  },
  truckIcon: {
    color: "#fff",
    fontSize: theme.typography.pxToRem(14)
  }
}));

const VehicleMenuListItem = memo(
  ({ vehicle, onClick, classes, searchInputValue, disabled, color }) => {
    const vehicleName = vehicle?.type_id?.name || "Unnamed vehicle";
    const [vehicleNameRef, isVehicleNameTruncated] = useIsTruncated([
      vehicleName
    ]);
    return (
      <ListItem
        key={vehicle.type_id.id}
        button
        className={classes.listItem}
        onClick={onClick}
        disabled={disabled}
      >
        <ListItemAvatar className={classes.listItemAvatar}>
          <Box
            className={classes.truckIconLink}
            style={{ backgroundColor: color }}
          >
            <DirectionsCar className={classes.truckIcon} />
          </Box>
        </ListItemAvatar>
        <OptimizedTooltip
          placement="bottom"
          title={isVehicleNameTruncated ? vehicleName : ""}
        >
          <ListItemText
            disableTypography
            ref={vehicleNameRef}
            className={classes.listItemText}
          >
            <FormattedText text={vehicleName} query={searchInputValue} />
          </ListItemText>
        </OptimizedTooltip>
      </ListItem>
    );
  }
);

const AssignVehicleMenu = props => {
  const {
    mainRouteId,
    anchorElement,
    onClose,
    vehicles,
    solution,
    projectId,
    // setChangedVehicles,
    setSolution,
    colorsObj,
    setLoadingRoutesList
  } = props;

  // General
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // States
  const [searchInputValue, setSearchInputValue] = useState("");
  const getColor = id => (colorsObj[id] >= 0 ? colorsObj[id] : 0);

  // Normalize API response to an array of routes
  const toRoutesArray = payload => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.routes)) return payload.routes;
    return [payload];
  };

  // Merge routes keeping a single route per vehicle_id, preferring optimized over created
  const mergeByVehicleIdPreferOptimized = routes => {
    const withoutVehicleId = [];
    const byVehicle = new Map();

    const pickPreferred = (first, second) => {
      if (!first) return second;
      if (!second) return first;
      const firstOptimized = first.status === "optimized";
      const secondOptimized = second.status === "optimized";
      if (firstOptimized && !secondOptimized) return first;
      if (secondOptimized && !firstOptimized) return second;
      // If equal priority, prefer the latest (second)
      return second;
    };

    routes.forEach(route => {
      if (!route) return;
      if (!route.vehicle_id) {
        withoutVehicleId.push(route);
        return;
      }
      const existing = byVehicle.get(route.vehicle_id);
      byVehicle.set(route.vehicle_id, pickPreferred(existing, route));
    });

    return [...withoutVehicleId, ...byVehicle.values()];
  };

  const assignVehicle = vehicleId => {
    onClose();
    setLoadingRoutesList(true);
    let relatedRouteIds = [];
    const routeGroup =
      solution.routeGroups[Object.keys(solution.routeGroups)[0]];
    routeGroup.forEach(routes => {
      const routeIds = routes.map(route => route.id);

      if (routeIds.includes(mainRouteId)) {
        relatedRouteIds = routeIds;
      }
    });
    relatedRouteIds = [mainRouteId];

    const newSolution = JSON.parse(JSON.stringify(solution));
    Promise.all(
      relatedRouteIds.map(theRouteId => {
        return API.patch(
          `/v1/projects/${projectId}/routes/${theRouteId}/vehicle`,
          { vehicleId }
        );
      })
    )
      .then(results => {
        const allUpdatedRoutes = [];
        results.forEach(res => {
          const routesArr = toRoutesArray(res?.data);

          // Notify WS per route
          routesArr.forEach(route => {
            client &&
              client.send(
                JSON.stringify({
                  type: "vehicle_change",
                  route_id: route.id,
                  vehicle_id: route.vehicle_id || vehicleId
                })
              );
          });

          allUpdatedRoutes.push(...routesArr);
        });

        // Clean and merge routes
        const cleaned = cleanSolutionWithNewRoutes(
          { ...newSolution },
          allUpdatedRoutes
        );

        // Keep a single entry per unique vehicle_id, preferring optimized
        cleaned.routes = mergeByVehicleIdPreferOptimized([
          ...cleaned.routes,
          ...allUpdatedRoutes
        ]);

        setSolution({ ...cleaned });
      })
      .catch(({ data }) => {
        const errors = data?.errors || [];
        if (errors.length > 0) {
          errors.forEach(error => {
            enqueueSnackbar(error.defaultMessage || "Error assigning vehicle", {
              variant: "error"
            });
          });
        }
      })
      .finally(() => {
        relatedRouteIds = [];
        setLoadingRoutesList(false);
      });
  };

  const filteredItems = useMemo(() => {
    if (!searchInputValue) {
      return vehicles;
    }
    return vehicles.filter(vehicle => {
      return (
        vehicle.type_id?.name?.toLowerCase().indexOf(searchInputValue) !== -1
      );
    });
  }, [vehicles, searchInputValue]);

  return (
    <>
      <Popover
        open={anchorElement !== null}
        onClose={() => {
          onClose();
          setTimeout(() => {
            setSearchInputValue("");
          }, 200);
        }}
        anchorEl={anchorElement}
        classes={{
          paper: classes.menuPaper
        }}
        PaperProps={{
          elevation: 0
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        <Grid
          container
          direction="column"
          className={classes.menuContentWrapper}
        >
          <Grid item className={classes.searchWrapper}>
            <OutlinedInput
              placeholder={"Assign to..."}
              fullWidth
              classes={{ input: classes.searchInput }}
              onChange={event => {
                const value = event.target.value.toLowerCase();
                setSearchInputValue(value);
              }}
            />
          </Grid>
          <Grid
            item
            alignItems={"center"}
            alignContent={"center"}
            justify={"center"}
            className={classes.listWrapper}
          >
            {filteredItems.length === 0 && (
              <Typography className={classes.noVehicleNotice}>
                No vehicles to display
              </Typography>
            )}
            {filteredItems.length > 0 && (
              <List className={classes.list}>
                {filteredItems.map(currentVehicle => {
                  const currentRoute = solution.routes.find(
                    route => route.vehicle_id === currentVehicle?.vehicle_id
                  );
                  const isVehicleAlreadyInUse = solution.routes.some(route => {
                    return (
                      route.vehicle_id === currentVehicle?.vehicle_id &&
                      route.stops.length > 2
                    );
                  });
                  const color =
                    currentRoute?.id && currentRoute?.stops.length > 2
                      ? routeColors[
                          getColor(currentRoute?.id) % routeColors.length
                        ]
                      : "#a0a0a0";
                  return (
                    <VehicleMenuListItem
                      key={currentVehicle.type_id.id}
                      vehicle={currentVehicle}
                      onClick={() => {
                        assignVehicle(currentVehicle.type_id.id);
                        setSearchInputValue("");
                      }}
                      classes={classes}
                      searchInputValue={searchInputValue}
                      disabled={isVehicleAlreadyInUse}
                      color={color}
                    />
                  );
                })}
              </List>
            )}
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

function mapStateToProps(state) {
  return {
    vehicles: state.project.vehicles,
    solution: state.project.solution,
    projectId: state.project.id,
    colorsObj: state.project.solution && state.project.solution.routeColors
  };
}

export default connect(mapStateToProps, {
  setSolution,
  setLoadingRoutesList,
  openAddVehicleDialog
})(AssignVehicleMenu);
